import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import '../CrmStyles.css';
import { LuFileText, LuPlus, LuTrash2, LuDownload, LuCircleAlert, LuX, LuPencil, LuCheck, LuSearch, LuShieldCheck, LuEye } from 'react-icons/lu';
import BASE_URL from '../../url';
import SearchableSelect from './SearchableSelect';
import CommercialDocumentModal from './CommercialDocumentModal';

const CompanyDocuments = ({ companyId, company, isEditMode = false }) => {
  // Pillar Selection: COMMERCIAL (Pillar A) vs SHELF (Pillar B)
  const [pillarTab, setPillarTab] = useState('COMMERCIAL');

  // Pillar A (Commercial Documents Engine) State
  const [commercialDocs, setCommercialDocs] = useState([]);
  const [commercialLoading, setCommercialLoading] = useState(false);
  const [commSearchTerm, setCommSearchTerm] = useState('');
  const [commTypeFilter, setCommTypeFilter] = useState('ALL');
  const [commStatusFilter, setCommStatusFilter] = useState('ALL');
  const [commModal, setCommModal] = useState({
    open: false,
    mode: 'CREATE_DRAFT',
    documentId: null,
    documentTypeCode: 'MSA'
  });

  // Pillar B (Shelf Documents) State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [includeMspDocuments, setIncludeMspDocuments] = useState(false);
  const [clientFilter, setClientFilter] = useState('');
  const [clientList, setClientList] = useState([]);

  const [formData, setFormData] = useState({
    documentTypeCode: 'MSA',
    title: '',
    clientCompanyId: '',
    effectiveFromDate: '',
    effectiveToDate: '',
    expiryAlertDays: 30,
    isAlertEnabled: true,
    ownerUserId: '',
    notes: '',
    fileUrl: '',
    fileName: '',
    fileSize: 0,
    mimeType: 'application/pdf'
  });
  const [error, setError] = useState('');

  // Inline row editing state
  const [editingDocId, setEditingDocId] = useState(null);
  const [editDocData, setEditDocData] = useState({});
  const [docRowSaving, setDocRowSaving] = useState(false);
  const [employees, setEmployees] = useState([]);

  const fetchClientList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/crm/companies?role=Client&status=Active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setClientList(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching client list:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      let token = localStorage.getItem('token');
      if (token && token.startsWith('Bearer ')) {
        token = token.replace('Bearer ', '');
      }

      // Fetch all employees from the Employee Directory endpoint
      const response = await axios.get(`${BASE_URL}/api/employees/company/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success && Array.isArray(response.data.data)) {
        const empList = response.data.data
          .map(emp => ({
            id: emp.EmployeeId || emp.Id || emp.id,
            name: emp.Name || emp.name || `${emp.FirstName || ''} ${emp.LastName || ''}`.trim() || emp.EmployeeCode,
            employeeCode: emp.EmployeeCode || emp.employeeCode || '',
            email: emp.Email || emp.email || '',
            position: emp.Position || emp.position || emp.JobTitle || '',
            status: emp.Status || emp.status || emp.EmploymentStatus || 'Active'
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        setEmployees(empList);
      } else {
        fetchUsersFallback();
      }
    } catch (err) {
      console.error('Error fetching employee directory:', err);
      fetchUsersFallback();
    }
  };

  const fetchUsersFallback = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.users) {
        const mapped = response.data.users.map(u => ({
          id: u.id,
          name: u.firstName || u.lastName ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : u.username,
          employeeCode: u.employeeId || '',
          email: u.email || '',
          position: u.role || ''
        }));
        setEmployees(mapped);
      }
    } catch (fallbackErr) {
      console.error('Fallback user fetch error:', fallbackErr);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchClientList();
  }, []);

  const handleDocEditClick = (d) => {
    setEditingDocId(d.CompanyDocumentId);
    setEditDocData({
      documentTypeCode: d.DocumentTypeCode || 'MSA',
      title: d.Title || '',
      clientCompanyId: d.ClientCompanyId || '',
      effectiveFromDate: d.EffectiveFromDate ? d.EffectiveFromDate.split('T')[0] : '',
      effectiveToDate: d.EffectiveToDate ? d.EffectiveToDate.split('T')[0] : '',
      expiryAlertDays: d.ExpiryAlertDays !== undefined ? d.ExpiryAlertDays : 30,
      isAlertEnabled: d.IsAlertEnabled !== false,
      ownerUserId: d.OwnerUserId || '',
      notes: d.Notes || ''
    });
  };

  const handleDocEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditDocData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'documentTypeCode' && value === 'SOW' && (prev.expiryAlertDays === 30 || !prev.expiryAlertDays)) {
        next.expiryAlertDays = 60;
      }
      return next;
    });
  };

  const handleSaveDoc = async (documentId) => {
    setDocRowSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${BASE_URL}/api/crm/companies/${companyId}/documents/${documentId}`,
        editDocData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setEditingDocId(null);
        fetchDocuments();
      } else {
        alert(response.data.message || 'Failed to save document.');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving document.');
    } finally {
      setDocRowSaving(false);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${BASE_URL}/api/crm/companies/${companyId}/documents?includeMspDocuments=${includeMspDocuments}`;
      if (clientFilter) {
        url += `&clientCompanyId=${clientFilter}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDocuments(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommercialDocs = async () => {
    if (!companyId) return;
    setCommercialLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/crm/commercial-documents?counterpartyCompanyId=${companyId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setCommercialDocs(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching commercial documents:', err);
    } finally {
      setCommercialLoading(false);
    }
  };

  const handleDeleteCommercialDoc = async (doc) => {
    const docIdentifier = doc.DocumentNumber || doc.Title || `#${doc.CommercialDocumentId}`;
    const result = await Swal.fire({
      title: 'Delete Document?',
      text: `Are you sure you want to delete ${docIdentifier}? This will remove it from the agreements list.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it'
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${BASE_URL}/api/crm/commercial-documents/${doc.CommercialDocumentId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: `Agreement ${docIdentifier} has been deleted.`,
          timer: 1500,
          showConfirmButton: false
        });
        fetchCommercialDocs();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: res.data.message || 'Could not delete agreement.'
        });
      }
    } catch (err) {
      console.error('Error deleting commercial document:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Error deleting agreement.'
      });
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchDocuments();
      fetchCommercialDocs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, includeMspDocuments, clientFilter]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      if (name === 'documentTypeCode' && value === 'SOW' && (prev.expiryAlertDays === 30 || !prev.expiryAlertDays)) {
        next.expiryAlertDays = 60;
      }
      return next;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream',
        fileUrl: `/uploads/company-docs/${file.name}`,
        fileObj: file
      }));
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.fileName) {
      setError('Please select a file to upload.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'fileObj' && formData.fileObj) {
          payload.append('file', formData.fileObj);
        } else if (key !== 'fileObj' && formData[key] !== null && formData[key] !== undefined) {
          payload.append(key, formData[key]);
        }
      });

      const response = await axios.post(`${BASE_URL}/api/crm/companies/${companyId}/documents`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setIsAdding(false);
        setFormData({
          documentTypeCode: 'MSA',
          title: '',
          clientCompanyId: '',
          effectiveFromDate: '',
          effectiveToDate: '',
          expiryAlertDays: 30,
          isAlertEnabled: true,
          ownerUserId: '',
          notes: '',
          fileUrl: '',
          fileName: '',
          fileSize: 0,
          mimeType: 'application/pdf'
        });
        fetchDocuments();
      } else {
        setError(response.data.message || 'Failed to upload document');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/crm/companies/${companyId}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  // Expiration calculation helper
  const getExpiryDetails = (toDate, alertDays = 30) => {
    if (!toDate) {
      return {
        statusKey: 'INDEFINITE',
        label: 'Indefinite (No Expiry)',
        color: '#475569',
        bgColor: '#f1f5f9'
      };
    }
    const expiry = new Date(toDate);
    const now = new Date();
    expiry.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diffTime = expiry - now;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      const overdue = Math.abs(diffDays);
      return {
        statusKey: 'EXPIRED',
        label: `Expired (${overdue} ${overdue === 1 ? 'day' : 'days'} ago)`,
        color: '#991b1b',
        bgColor: '#fee2e2',
        isDanger: true
      };
    } else if (diffDays <= alertDays) {
      return {
        statusKey: 'EXPIRING',
        label: diffDays === 0 ? 'Expires Today' : `Expiring Soon (${diffDays} ${diffDays === 1 ? 'day' : 'days'} left)`,
        color: '#92400e',
        bgColor: '#fef3c7',
        isWarning: true
      };
    } else {
      return {
        statusKey: 'VALID',
        label: `Valid (${diffDays} days left)`,
        color: '#065f46',
        bgColor: '#eff6ff',
        isValid: true
      };
    }
  };

  // Keep existing helper signatures for full compatibility
  const isExpiringSoon = (toDate, alertDays) => {
    return getExpiryDetails(toDate, alertDays).statusKey === 'EXPIRING';
  };

  const isExpired = (toDate) => {
    return getExpiryDetails(toDate).statusKey === 'EXPIRED';
  };

  const getDocTypeBadgeStyle = (code) => {
    switch (code) {
      case 'MSA':
        return { background: '#e0e7ff', color: '#3730a3' }; // Indigo
      case 'NDA':
        return { background: '#ede9fe', color: '#5b21b6' }; // Purple
      case 'SOW':
        return { background: '#dbeafe', color: '#1e40af' }; // Blue
      case 'PO':
        return { background: '#e0f2fe', color: '#0369a1' }; // Sky
      case 'CONTRACT':
        return { background: '#eff6ff', color: '#1d4ed8' }; // Teal
      case 'W9':
        return { background: '#f3f4f6', color: '#374151' }; // Gray
      case 'INSURANCE':
        return { background: '#ffedd5', color: '#9a3412' }; // Orange
      default:
        return { background: '#f1f5f9', color: '#475569' }; // Slate
    }
  };

  // Filtered documents list
  const filteredDocuments = useMemo(() => {
    return documents.filter(d => {
      const matchesSearch = !searchTerm.trim() || 
        (d.Title && d.Title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (d.FileName && d.FileName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (d.DocumentTypeCode && d.DocumentTypeCode.toLowerCase().includes(searchTerm.toLowerCase()));

      if (!matchesSearch) return false;

      if (statusFilter === 'ALL') return true;
      const status = getExpiryDetails(d.EffectiveToDate, d.ExpiryAlertDays).statusKey;
      return status === statusFilter;
    });
  }, [documents, searchTerm, statusFilter]);

  const getCommStatusBadge = (status) => {
    switch (status) {
      case 'DRAFT':
        return { bg: '#f1f5f9', color: '#475569', label: 'Draft' };
      case 'INTERNAL_REVIEW':
      case 'PENDING_REVIEW':
        return { bg: '#fef3c7', color: '#92400e', label: 'In Review' };
      case 'SENT_FOR_SIGNATURE':
        return { bg: '#e0f2fe', color: '#0369a1', label: 'Sent for Sign' };
      case 'PARTIALLY_SIGNED':
        return { bg: '#f3e8ff', color: '#6b21a8', label: 'Partially Signed' };
      case 'EXECUTED':
      case 'ACTIVE':
        return { bg: '#dcfce7', color: '#166534', label: 'Executed ✓' };
      case 'REJECTED':
      case 'CANCELLED':
        return { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' };
      case 'EXPIRED':
        return { bg: '#fee2e2', color: '#b91c1c', label: 'Expired' };
      default:
        return { bg: '#f1f5f9', color: '#64748b', label: status || 'Draft' };
    }
  };

  const filteredCommercialDocs = useMemo(() => {
    return commercialDocs.filter(d => {
      const matchesSearch = !commSearchTerm.trim() || 
        (d.DocumentNumber && d.DocumentNumber.toLowerCase().includes(commSearchTerm.toLowerCase())) ||
        (d.Title && d.Title.toLowerCase().includes(commSearchTerm.toLowerCase())) ||
        (d.DocumentTypeCode && d.DocumentTypeCode.toLowerCase().includes(commSearchTerm.toLowerCase()));

      if (!matchesSearch) return false;
      if (commTypeFilter !== 'ALL' && d.DocumentTypeCode !== commTypeFilter) return false;
      if (commStatusFilter !== 'ALL' && d.StatusCode !== commStatusFilter) return false;
      return true;
    });
  }, [commercialDocs, commSearchTerm, commTypeFilter, commStatusFilter]);

  return (
    <div className="crm-card" style={{ marginTop: '24px' }}>
      <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LuFileText size={18} color="#3b5bfd" /> Documents & Agreements
          </h3>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {pillarTab === 'COMMERCIAL' ? (
            <button 
              type="button"
              onClick={() => setCommModal({
                open: true,
                mode: 'CREATE_DRAFT',
                documentId: null,
                documentTypeCode: company?.IsSupplier ? 'MSA' : 'MSA'
              })}
              className="crm-btn-primary" 
              style={{ padding: '6px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <LuPlus size={16} /> Draft New Agreement
            </button>
          ) : (
            <button 
              type="button"
              onClick={() => setIsAdding(!isAdding)} 
              className={isAdding ? "crm-btn-danger" : "crm-btn-primary"} 
              style={{ padding: '6px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {isAdding ? <><LuX size={16} /> Cancel</> : <><LuPlus size={16} /> Add Document</>}
            </button>
          )}
        </div>
      </div>

      {/* Sub-Tabs: Pillar A (Commercial Engine) vs Pillar B (Shelf Documents) */}
      <div style={{
        display: 'flex',
        gap: '4px',
        borderBottom: '1px solid #e2e8f0',
        padding: '0 24px',
        backgroundColor: '#f8fafc'
      }}>
        <button
          type="button"
          onClick={() => setPillarTab('COMMERCIAL')}
          style={{
            padding: '12px 18px',
            border: 'none',
            background: 'none',
            fontSize: '14px',
            fontWeight: pillarTab === 'COMMERCIAL' ? 600 : 500,
            color: pillarTab === 'COMMERCIAL' ? '#3b5bfd' : '#64748b',
            borderBottom: pillarTab === 'COMMERCIAL' ? '2px solid #3b5bfd' : '2px solid transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <LuShieldCheck size={16} /> Commercial Agreements (Pillar A)
          <span style={{
            fontSize: '11px',
            background: pillarTab === 'COMMERCIAL' ? 'rgba(1, 157, 136, 0.12)' : '#e2e8f0',
            color: pillarTab === 'COMMERCIAL' ? '#3b5bfd' : '#64748b',
            padding: '2px 7px',
            borderRadius: '10px',
            fontWeight: 700
          }}>
            {commercialDocs.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setPillarTab('SHELF')}
          style={{
            padding: '12px 18px',
            border: 'none',
            background: 'none',
            fontSize: '14px',
            fontWeight: pillarTab === 'SHELF' ? 600 : 500,
            color: pillarTab === 'SHELF' ? '#3b5bfd' : '#64748b',
            borderBottom: pillarTab === 'SHELF' ? '2px solid #3b5bfd' : '2px solid transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <LuFileText size={16} /> Compliance Shelf (Pillar B)
          <span style={{
            fontSize: '11px',
            background: pillarTab === 'SHELF' ? 'rgba(1, 157, 136, 0.12)' : '#e2e8f0',
            color: pillarTab === 'SHELF' ? '#3b5bfd' : '#64748b',
            padding: '2px 7px',
            borderRadius: '10px',
            fontWeight: 700
          }}>
            {documents.length}
          </span>
        </button>
      </div>

      <div className="crm-card-body" style={{ padding: '0 24px 24px 24px' }}>
        {pillarTab === 'COMMERCIAL' ? (
          <div>
            {/* Filter toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '18px 0', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1, minWidth: '260px' }}>
                <div style={{ position: 'relative', width: '100%', maxWidth: '320px' }}>
                  <LuSearch size={16} style={{ position: 'absolute', left: '10px', top: '10px', color: '#94a3b8' }} />
                  <input
                    type="text"
                    placeholder="Search by Document # or Title..."
                    className="crm-input"
                    value={commSearchTerm}
                    onChange={(e) => setCommSearchTerm(e.target.value)}
                    style={{ paddingLeft: '32px' }}
                  />
                </div>
                <select
                  className="crm-select"
                  value={commTypeFilter}
                  onChange={(e) => setCommTypeFilter(e.target.value)}
                  style={{ width: '130px' }}
                >
                  <option value="ALL">All Types</option>
                  <option value="MSA">MSA</option>
                  <option value="PO">PO</option>
                  <option value="SOW">SOW</option>
                </select>
                <select
                  className="crm-select"
                  value={commStatusFilter}
                  onChange={(e) => setCommStatusFilter(e.target.value)}
                  style={{ width: '150px' }}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="DRAFT">Draft</option>
                  <option value="SENT_FOR_SIGNATURE">Sent for Sign</option>
                  <option value="EXECUTED">Executed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Commercial Agreements Table */}
            <div className="crm-table-container">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th>Document #</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Side</th>
                    <th>Status</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Modified Date</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCommercialDocs.map(doc => {
                    const statusBadge = getCommStatusBadge(doc.StatusCode);
                    const startDateStr = doc.EffectiveFromDate 
                      ? new Date(doc.EffectiveFromDate).toLocaleDateString() 
                      : (doc.CreatedAtUtc ? new Date(doc.CreatedAtUtc).toLocaleDateString() : '—');
                    const endDateStr = doc.EffectiveToDate 
                      ? new Date(doc.EffectiveToDate).toLocaleDateString() 
                      : '—';
                    const modifiedDateStr = doc.UpdatedAtUtc 
                      ? new Date(doc.UpdatedAtUtc).toLocaleDateString() 
                      : (doc.CreatedAtUtc ? new Date(doc.CreatedAtUtc).toLocaleDateString() : '—');

                    return (
                      <tr key={doc.CommercialDocumentId}>
                        <td style={{ fontWeight: 600, color: '#0369a1' }}>
                          {doc.DocumentNumber || `#${doc.CommercialDocumentId}`}
                        </td>
                        <td>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 700,
                            ...getDocTypeBadgeStyle(doc.DocumentTypeCode)
                          }}>
                            {doc.DocumentTypeCode}
                          </span>
                        </td>
                        <td style={{ fontWeight: 500, color: '#1e293b' }}>
                          {doc.Title || `${doc.DocumentTypeCode} Agreement`}
                        </td>
                        <td>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            {doc.CounterpartySide || (company?.IsSupplier ? 'Supplier' : 'Client')}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: statusBadge.bg,
                            color: statusBadge.color
                          }}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td style={{ fontSize: '12px', color: '#64748b' }}>
                          {startDateStr}
                        </td>
                        <td style={{ fontSize: '12px', color: '#64748b' }}>
                          {endDateStr}
                        </td>
                        <td style={{ fontSize: '12px', color: '#64748b' }}>
                          {modifiedDateStr}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                            <button
                              type="button"
                              onClick={() => setCommModal({
                                open: true,
                                mode: 'VIEW_AND_SIGN',
                                documentId: doc.CommercialDocumentId,
                                documentTypeCode: doc.DocumentTypeCode,
                                autoEdit: false
                              })}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#eff6ff',
                                color: '#166534',
                                border: '1px solid #bbf7d0',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11.5px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              title="View & Sign Document"
                            >
                              <LuEye size={13} /> View & Sign
                            </button>
                            <button
                              type="button"
                              onClick={() => setCommModal({
                                open: true,
                                mode: 'EDIT',
                                documentId: doc.CommercialDocumentId,
                                documentTypeCode: doc.DocumentTypeCode,
                                autoEdit: true
                              })}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#f0f9ff',
                                color: '#0369a1',
                                border: '1px solid #bae6fd',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11.5px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              title="Edit Agreement Details & Terms"
                            >
                              <LuPencil size={13} /> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteCommercialDoc(doc)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#fef2f2',
                                color: '#b91c1c',
                                border: '1px solid #fecaca',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11.5px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              title="Delete Agreement"
                            >
                              <LuTrash2 size={13} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filteredCommercialDocs.length === 0 && !commercialLoading && (
                    <tr>
                      <td colSpan="9" style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
                        <div>No commercial agreements found. Click <strong>+ Draft New Agreement</strong> to create one.</div>
                      </td>
                    </tr>
                  )}

                  {commercialLoading && (
                    <tr>
                      <td colSpan="9" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                        Loading commercial agreements...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div>
        {/* Upload Form */}
        {isAdding && (
          <form onSubmit={handleAddDocument} style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', margin: '20px 0', border: '1px solid #e2e8f0', display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr' }}>
            <div style={{ gridColumn: '1 / -1', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>Upload New Document</span>
              <span style={{ fontSize: '12px', color: '#64748b' }}>* Required fields</span>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>Document Type *</label>
              <select name="documentTypeCode" className="crm-select" value={formData.documentTypeCode} onChange={handleChange} required>
                <option value="MSA">MSA (Master Service Agreement)</option>
                <option value="NDA">NDA (Non-Disclosure Agreement)</option>
                <option value="SOW">SOW (Statement of Work)</option>
                <option value="PO">PO (Purchase Order)</option>
                <option value="CONTRACT">Contract / Agreement</option>
                <option value="W9">W-9 Tax Form</option>
                <option value="INSURANCE">Insurance Certificate (COI)</option>
                <option value="OTHER">Other Document</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>Document Title (Optional)</label>
              <input type="text" name="title" className="crm-input" placeholder="e.g. Master Services Agreement 2026" value={formData.title} onChange={handleChange} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>
                {company?.IsMSP ? (
                  <span style={{ color: '#d97706', fontWeight: 600 }}>Client this document is for (Recommended)</span>
                ) : (
                  <span>Related Client (Optional)</span>
                )}
              </label>
              <SearchableSelect
                options={clientList.map(c => ({
                  value: c.CompanyId,
                  label: `${c.DisplayName || c.CompanyName} (${c.CompanyCode})`,
                  subLabel: c.CompanyCode
                }))}
                value={formData.clientCompanyId}
                onChange={(val) => setFormData(prev => ({ ...prev, clientCompanyId: val }))}
                placeholder="-- No specific client / General --"
                searchPlaceholder="Search client by name or code..."
                allowClear={true}
                triggerStyle={company?.IsMSP ? { borderColor: '#f59e0b', backgroundColor: '#fffbeb' } : {}}
              />
              <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                {company?.IsMSP 
                  ? 'Tag which client this MSP agreement or placement relates to (e.g. TCS)' 
                  : 'Link this document to a specific client if applicable'}
              </span>
            </div>
            
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>Upload File (PDF, DOC, DOCX, Images) *</label>
              <input type="file" onChange={handleFileChange} className="crm-input" style={{ padding: '8px' }} required />
              {formData.fileName && (
                <div style={{ fontSize: '12px', color: '#3b5bfd', marginTop: '4px', fontWeight: '500' }}>
                  Selected: {formData.fileName} ({Math.round(formData.fileSize / 1024)} KB)
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>Effective From (Start Date)</label>
              <input type="date" name="effectiveFromDate" className="crm-input" value={formData.effectiveFromDate} onChange={handleChange} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>Effective To (Expiration Date)</label>
              <input type="date" name="effectiveToDate" className="crm-input" value={formData.effectiveToDate} onChange={handleChange} />
              <span style={{ fontSize: '11px', color: '#64748b' }}>Leave blank if permanent / indefinite</span>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>Alert Days Before Expiry</label>
              <input type="number" name="expiryAlertDays" className="crm-input" value={formData.expiryAlertDays} onChange={handleChange} min="1" max="180" />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>Alert Owner (Notifications)</label>
              <SearchableSelect
                options={employees.map(emp => ({
                  value: emp.id,
                  label: `${emp.name}${emp.employeeCode ? ` (${emp.employeeCode})` : ''}`,
                  subLabel: emp.position ? emp.position : (emp.email ? emp.email : '')
                }))}
                value={formData.ownerUserId}
                onChange={(val) => setFormData(prev => ({ ...prev, ownerUserId: val }))}
                placeholder="Select Employee"
                searchPlaceholder="Search employee..."
                allowClear={true}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gridColumn: '1 / -1', padding: '4px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#334155' }}>
                <input type="checkbox" name="isAlertEnabled" checked={formData.isAlertEnabled} onChange={handleChange} style={{ accentColor: '#3b5bfd', width: '16px', height: '16px' }} />
                <span><strong>Enable Expiry Notifications</strong> (Alert assigned owner before document expires)</span>
              </label>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563', fontWeight: '500' }}>Notes / Remarks</label>
              <textarea name="notes" className="crm-input" value={formData.notes} onChange={handleChange} rows="2" placeholder="Any special agreement clauses or comments..."></textarea>
            </div>
            
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', alignItems: 'center', marginTop: '4px' }}>
              <button type="submit" className="crm-btn-primary" style={{ padding: '8px 16px' }}>Upload Document</button>
              <button type="button" onClick={() => setIsAdding(false)} className="crm-btn-secondary" style={{ padding: '8px 16px' }}>Cancel</button>
              {error && <span style={{ color: '#ef4444', fontSize: '13px' }}>{error}</span>}
            </div>
          </form>
        )}

        {/* Filter & Search Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '320px' }}>
            <LuSearch size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input 
              type="text" 
              className="crm-input" 
              placeholder="Search documents by title, file, type..." 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '32px', height: '36px', fontSize: '13px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Filter by Client (Shown when viewing an MSP or when any docs have clients) */}
            {(company?.IsMSP || documents.some(d => d.ClientCompanyId)) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Client:</span>
                <SearchableSelect
                  options={clientList.map(c => ({
                    value: c.CompanyId,
                    label: c.DisplayName || c.CompanyName,
                    subLabel: c.CompanyCode
                  }))}
                  value={clientFilter}
                  onChange={(val) => setClientFilter(val)}
                  placeholder="All Clients"
                  searchPlaceholder="Search client..."
                  allowClear={true}
                  style={{ minWidth: '180px', maxWidth: '260px' }}
                  triggerStyle={{ height: '36px', minHeight: '36px', fontSize: '13px' }}
                  menuAlign="left"
                />
              </div>
            )}

            {/* Toggle: Include documents filed under MSPs (for Clients) */}
            {company?.IsClient && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#334155', cursor: 'pointer', background: '#f8fafc', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <input 
                  type="checkbox" 
                  checked={includeMspDocuments} 
                  onChange={e => setIncludeMspDocuments(e.target.checked)}
                  style={{ accentColor: '#3b5bfd', width: '16px', height: '16px' }}
                />
                <span style={{ fontWeight: 500 }}>Include documents filed under MSPs</span>
              </label>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Status:</span>
              <select 
                value={statusFilter} 
                onChange={e => setStatusFilter(e.target.value)} 
                className="crm-select" 
                style={{ height: '36px', padding: '4px 10px', fontSize: '13px', minWidth: '130px' }}
              >
                <option value="ALL">All Documents</option>
                <option value="VALID">Valid</option>
                <option value="EXPIRING">Expiring Soon</option>
                <option value="EXPIRED">Expired</option>
                <option value="INDEFINITE">Indefinite</option>
              </select>
            </div>
          </div>
        </div>

        {/* Documents Table */}
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <table className="crm-table" style={{ margin: 0 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '12px 16px' }}>Document & File</th>
                <th style={{ padding: '12px 16px' }}>Type</th>
                <th style={{ padding: '12px 16px' }}>Related Client</th>
                <th style={{ padding: '12px 16px' }}>Source</th>
                <th style={{ padding: '12px 16px' }}>Validity Period</th>
                <th style={{ padding: '12px 16px' }}>Expiration Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map(d => {
                const expiryInfo = getExpiryDetails(d.EffectiveToDate, d.ExpiryAlertDays);
                const isRowEditing = editingDocId === d.CompanyDocumentId;
                
                // Lookup owner name from employee directory or server OwnerUserName
                const ownerEmp = employees.find(e => String(e.id) === String(d.OwnerUserId));
                const ownerDisplayName = ownerEmp 
                  ? `${ownerEmp.name}${ownerEmp.employeeCode ? ` (${ownerEmp.employeeCode})` : ''}` 
                  : (d.OwnerUserName ? `${d.OwnerUserName}${d.OwnerEmployeeCode ? ` (${d.OwnerEmployeeCode})` : ''}` : (d.OwnerUserId ? `Employee #${d.OwnerUserId}` : 'Unassigned'));

                return (
                  <tr key={d.CompanyDocumentId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    {/* Column 1: Document title & filename */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                      {isRowEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <input 
                            type="text" 
                            name="title" 
                            className="crm-input" 
                            value={editDocData.title} 
                            onChange={handleDocEditChange} 
                            placeholder="Document title" 
                            style={{ fontSize: '13px' }}
                          />
                          <input 
                            type="text" 
                            name="notes" 
                            className="crm-input" 
                            value={editDocData.notes} 
                            onChange={handleDocEditChange} 
                            placeholder="Notes / remarks" 
                            style={{ fontSize: '12px' }}
                          />
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                            {d.Title || d.FileName}
                          </div>
                          <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📄 {d.FileName}</span>
                            {d.FileSize > 0 && <span>• {Math.round(d.FileSize / 1024)} KB</span>}
                          </div>
                          {d.Notes && (
                            <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '3px', fontStyle: 'italic' }}>
                              Note: {d.Notes}
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Column 2: Document Type */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                      {isRowEditing ? (
                        <select 
                          name="documentTypeCode" 
                          className="crm-select" 
                          value={editDocData.documentTypeCode} 
                          onChange={handleDocEditChange} 
                          style={{ fontSize: '12px', padding: '4px 6px' }}
                        >
                          <option value="MSA">MSA</option>
                          <option value="NDA">NDA</option>
                          <option value="SOW">SOW</option>
                          <option value="PO">PO</option>
                          <option value="CONTRACT">CONTRACT</option>
                          <option value="W9">W9</option>
                          <option value="COI">COI</option>
                          <option value="INSURANCE">INSURANCE</option>
                          <option value="VOID_CHECK">VOID_CHECK</option>
                          <option value="DIRECT_DEPOSIT">DIRECT_DEPOSIT</option>
                          <option value="ARTICLES_OF_INCORPORATION">ARTICLES_OF_INCORPORATION</option>
                          <option value="CERT_GOOD_STANDING">CERT_GOOD_STANDING</option>
                          <option value="COMPLIANCE">COMPLIANCE</option>
                          <option value="OTHER">OTHER</option>
                        </select>
                      ) : (
                        <span className="crm-badge" style={{ ...getDocTypeBadgeStyle(d.DocumentTypeCode), fontWeight: '600' }}>
                          {d.DocumentTypeCode}
                        </span>
                      )}
                    </td>

                    {/* Column 2.5: Related Client */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                      {isRowEditing ? (
                        <SearchableSelect
                          options={clientList.map(c => ({
                            value: c.CompanyId,
                            label: c.DisplayName || c.CompanyName,
                            subLabel: c.CompanyCode
                          }))}
                          value={editDocData.clientCompanyId || ''}
                          onChange={(val) => setEditDocData(prev => ({ ...prev, clientCompanyId: val }))}
                          placeholder="-- General (None) --"
                          searchPlaceholder="Search client..."
                          allowClear={true}
                          style={{ minWidth: '160px' }}
                          triggerStyle={{ height: '32px', minHeight: '32px', fontSize: '12px', padding: '2px 8px' }}
                        />
                      ) : d.ClientCompanyName ? (
                        <div>
                          <span className="crm-badge" style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                            🏢 {d.ClientCompanyName}
                          </span>
                          {String(d.CompanyId) !== String(companyId) && (
                            <div style={{ fontSize: '11px', color: '#0284c7', marginTop: '2px', fontWeight: 500 }}>
                              Via MSP: {d.CompanyName || 'MSP'}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                      )}
                    </td>

                    {/* Column 3: Source */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                      {d.SourceType === 'ONBOARDING' && (
                        <span className="crm-badge" style={{ background: '#e0e7ff', color: '#3730a3', fontSize: '11px', fontWeight: 600 }}>📋 Onboarding</span>
                      )}
                      {d.SourceType === 'SUPPLIER_SIGNED' && (
                        <span className="crm-badge" style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '11px' }}>✍️ Signed</span>
                      )}
                      {d.SourceType === 'SUPPLIER_PORTAL' && (
                        <span className="crm-badge" style={{ background: '#dbeafe', color: '#1e40af', fontSize: '11px' }}>📁 Supplier</span>
                      )}
                      {(!d.SourceType || d.SourceType === 'ADMIN') && (
                        <span className="crm-badge" style={{ background: '#f3f4f6', color: '#64748b', fontSize: '11px' }}>Admin</span>
                      )}
                    </td>

                    {/* Column 4: Validity & Alert Owner */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'top', fontSize: '13px', color: '#475569' }}>
                      {isRowEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>Start:</span>
                            <input type="date" name="effectiveFromDate" className="crm-input" value={editDocData.effectiveFromDate} onChange={handleDocEditChange} style={{ fontSize: '12px' }} />
                          </div>
                          <div>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>Expires:</span>
                            <input type="date" name="effectiveToDate" className="crm-input" value={editDocData.effectiveToDate} onChange={handleDocEditChange} style={{ fontSize: '12px' }} />
                          </div>
                          <div>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>Alert Days:</span>
                            <input type="number" name="expiryAlertDays" className="crm-input" value={editDocData.expiryAlertDays} onChange={handleDocEditChange} min="1" max="180" style={{ fontSize: '12px' }} />
                          </div>
                          <div>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>Owner:</span>
                            <SearchableSelect
                              options={employees.map(emp => ({
                                value: emp.id,
                                label: `${emp.name}${emp.employeeCode ? ` (${emp.employeeCode})` : ''}`,
                                subLabel: emp.position ? emp.position : (emp.email ? emp.email : '')
                              }))}
                              value={editDocData.ownerUserId || ''}
                              onChange={(val) => setEditDocData(prev => ({ ...prev, ownerUserId: val }))}
                              placeholder="Select Employee"
                              searchPlaceholder="Search employee..."
                              allowClear={true}
                              style={{ minWidth: '150px' }}
                              triggerStyle={{ height: '30px', minHeight: '30px', fontSize: '12px', padding: '2px 8px' }}
                            />
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                            <input type="checkbox" name="isAlertEnabled" checked={editDocData.isAlertEnabled} onChange={handleDocEditChange} style={{ accentColor: '#3b5bfd' }} />
                            Alert Enabled
                          </label>
                        </div>
                      ) : (
                        <div>
                          <div>
                            <strong>{d.EffectiveFromDate ? new Date(d.EffectiveFromDate).toLocaleDateString() : '—'}</strong>
                            {'  to  '}
                            <strong>{d.EffectiveToDate ? new Date(d.EffectiveToDate).toLocaleDateString() : 'Indefinite'}</strong>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>Alert Owner: <strong>{ownerDisplayName}</strong></span>
                            {d.IsAlertEnabled === false && (
                              <span style={{ color: '#94a3b8' }}>(Alerts off)</span>
                            )}
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Column 5: Status Pill */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                      <span 
                        className="crm-badge" 
                        style={{ 
                          background: expiryInfo.bgColor, 
                          color: expiryInfo.color, 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '5px', 
                          fontWeight: '600',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}
                      >
                        {expiryInfo.isDanger ? (
                          <LuCircleAlert size={13} />
                        ) : expiryInfo.isWarning ? (
                          <LuCircleAlert size={13} />
                        ) : expiryInfo.isValid ? (
                          <LuCheck size={13} />
                        ) : null}
                        {expiryInfo.label}
                      </span>
                    </td>

                    {/* Column 6: Actions */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'top', textAlign: 'right' }}>
                      {isRowEditing ? (
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <button 
                            onClick={() => handleSaveDoc(d.CompanyDocumentId)} 
                            className="crm-btn-primary" 
                            style={{ padding: '4px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }} 
                            disabled={docRowSaving}
                          >
                            <LuCheck size={14} /> {docRowSaving ? '...' : 'Save'}
                          </button>
                          <button 
                            onClick={() => setEditingDocId(null)} 
                            className="crm-btn-secondary" 
                            style={{ padding: '4px 8px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }} 
                            disabled={docRowSaving}
                          >
                            <LuX size={14} /> Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button 
                            onClick={() => handleDocEditClick(d)} 
                            style={{ background: 'none', border: 'none', color: '#3b5bfd', cursor: 'pointer', padding: '6px', borderRadius: '4px' }} 
                            title="Edit Document"
                          >
                            <LuPencil size={16} />
                          </button>
                          <button 
                            onClick={() => window.open(d.FileUrl.startsWith('http') ? d.FileUrl : `${BASE_URL}${d.FileUrl}`, '_blank')} 
                            style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: '6px', borderRadius: '4px' }} 
                            title="Download / View File"
                          >
                            <LuDownload size={16} />
                          </button>
                          <button 
                            onClick={() => handleDelete(d.CompanyDocumentId)} 
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px', borderRadius: '4px' }} 
                            title="Delete Document"
                          >
                            <LuTrash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {filteredDocuments.length === 0 && !loading && (
                <tr>
                  <td colSpan="7" style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
                    {searchTerm || statusFilter !== 'ALL' ? (
                      <div>No documents match your filter criteria.</div>
                    ) : (
                      <div>No documents uploaded yet. Click <strong>+ Add Document</strong> to upload company agreements.</div>
                    )}
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                    Loading documents...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
      </div>

      {/* Commercial Document Modal for Draft / View & Sign */}
      {commModal.open && (
        <CommercialDocumentModal
          isOpen={commModal.open}
          onClose={() => setCommModal(prev => ({ ...prev, open: false }))}
          initialMode={commModal.mode}
          autoEdit={Boolean(commModal.autoEdit)}
          documentId={commModal.documentId}
          documentTypeCode={commModal.documentTypeCode}
          counterpartySide={company?.IsSupplier ? 'SUPPLIER' : 'CLIENT'}
          counterpartyCompanyId={companyId}
          counterpartyCompanyName={company?.DisplayName || company?.CompanyName || ''}
          governingEntityId={4}
          onCreatedSuccess={() => {
            fetchCommercialDocs();
            setCommModal(prev => ({ ...prev, open: false }));
          }}
          onLinkedSuccess={() => {
            fetchCommercialDocs();
            setCommModal(prev => ({ ...prev, open: false }));
          }}
        />
      )}
    </div>
  );
};

export default CompanyDocuments;
